import apiClient, { getApiErrorInfo } from './api';

/** Which integration delivered the event — derived server-side from the event type. */
export type WebhookEventSource = 'chargebee' | 'docebo' | 'requirements-monitor';

/** Processing status as written by the handlers. `pending`/`processing` that never resolve usually mean a crashed handler. */
export type WebhookEventStatus = 'success' | 'failed' | 'pending' | 'processing';

export const WEBHOOK_EVENT_SOURCES: WebhookEventSource[] = ['chargebee', 'docebo', 'requirements-monitor'];
export const WEBHOOK_EVENT_STATUSES: WebhookEventStatus[] = ['success', 'failed', 'pending', 'processing'];

export const WEBHOOK_SOURCE_LABELS: Record<WebhookEventSource, string> = {
  chargebee: 'Chargebee',
  docebo: 'Docebo',
  'requirements-monitor': 'Requirements monitor',
};

export interface WebhookEventSummary {
  id: string;
  eventId: string;
  eventType: string;
  source: WebhookEventSource;
  status: WebhookEventStatus | string;
  /** First line of the error, if processing failed. */
  errorSummary: string | null;
  /** Who/what the event was about, extracted from the payload (may be null). */
  subject: string | null;
  processingDurationMs: number | null;
  receivedAt: string;
  processedAt: string | null;
  attemptCount: number;
  canRetry: boolean;
}

export interface WebhookEventDetail extends WebhookEventSummary {
  payload: string;
  errorMessage: string | null;
}

export interface WebhookEventPage {
  items: WebhookEventSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface WebhookEventSourceStats {
  source: WebhookEventSource;
  received: number;
  failed: number;
  lastReceivedAt: string | null;
}

export interface WebhookEventStats {
  windowHours: number;
  received: number;
  succeeded: number;
  failed: number;
  inFlight: number;
  failedTotal: number;
  bySource: WebhookEventSourceStats[];
  eventTypes: string[];
}

export interface WebhookEventRetryResult {
  event: WebhookEventDetail;
  succeeded: boolean;
}

export interface WebhookEventQuery {
  source?: WebhookEventSource | '';
  status?: WebhookEventStatus | '';
  eventType?: string;
  search?: string;
  /** ISO-8601 instants. */
  from?: string;
  to?: string;
  /** 1-based. */
  page?: number;
  pageSize?: number;
}

const BASE_PATH = '/api/admin/webhook-events';

/** Drop empty filters so the query string only carries what the admin actually set. */
function toParams(query: WebhookEventQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params[key] = value as string | number;
  }
  return params;
}

class WebhookEventService {
  async getEvents(query: WebhookEventQuery = {}): Promise<WebhookEventPage> {
    try {
      const response = await apiClient.get<WebhookEventPage>(BASE_PATH, { params: toParams(query) });
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load webhook events', { cause: error });
    }
  }

  async getStats(hours = 24): Promise<WebhookEventStats> {
    try {
      const response = await apiClient.get<WebhookEventStats>(`${BASE_PATH}/stats`, { params: { hours } });
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load webhook event stats', { cause: error });
    }
  }

  async getEvent(id: string): Promise<WebhookEventDetail> {
    try {
      const response = await apiClient.get<WebhookEventDetail>(`${BASE_PATH}/${id}`);
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to load webhook event', { cause: error });
    }
  }

  /** Re-dispatches a failed/stuck event to its processor. The API answers 409 when it already succeeded. */
  async retryEvent(id: string): Promise<WebhookEventRetryResult> {
    try {
      const response = await apiClient.post<WebhookEventRetryResult>(`${BASE_PATH}/${id}/retry`);
      return response.data;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      throw new Error(apiError.message || 'Failed to retry webhook event', { cause: error });
    }
  }
}

export default new WebhookEventService();
